import React, { FC } from "react";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PageHeading from "../Common/PageHeading/PageHeading";
import PageContent from "../Common/PageContent/PageContent";
import { TERMS_AND_CONDITIONS } from "../pageRoutes";

const sectionTitleSx = {
  fontWeight: 600,
  marginTop: 3,
  marginBottom: 1.5
};

const bodySx = {
  marginBottom: 2,
  textAlign: "left"
};

export const PrivacyPolicy: FC = () => {
  return (
    <section>
      <PageHeading titleText="Privacy Policy" />
      <PageContent styles={{ background: "white", paddingTop: "2em" }}>
        <Typography
          variant="body2"
          sx={{ marginBottom: 3, fontStyle: "italic" }}
        >
          Last Updated: <i>March 15, 2026</i>
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Kivunova Ltd respects your privacy and is committed to protecting
          personal data. This Privacy Policy explains how we collect, use,
          store, disclose, and safeguard personal data when individuals access
          or use our services.
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          This Privacy Policy is intended to comply with Law{" "}
          <i>No 058/2021 of 13/10/2021</i> relating to the protection of
          personal data and privacy in Rwanda, as well as applicable
          international data protection standards.
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          By accessing or using our Services, you acknowledge that you have read
          and understood this Privacy Policy and agree to the practices
          described herein. For the terms governing your use of our Services,
          please see our{" "}
          <Link
            component={RouterLink}
            to={TERMS_AND_CONDITIONS}
            underline="hover"
          >
            Terms and Conditions
          </Link>
          .
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Data Controller
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Kivunova Ltd. acts as the data controller responsible for the
          processing of personal data described in this Privacy Policy. As the
          data controller, Kivunova determines the purposes and means by which
          personal data is processed in connection with the Services.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Personal Data We Collect
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          In order to operate and provide our Services, we may collect and
          process certain personal data relating to users of the platform. This
          may include basic account information such as a user’s name, email
          address, phone number, and login credentials used for authentication.
          We may also collect business-related information provided by
          organizations using the platform, including company or store
          identifiers, operational information, and transaction data necessary
          for inventory and order management functions.
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Additionally, technical and usage information may be collected
          automatically when users interact with the Services. This information
          may include internet protocol addresses, device and browser
          information, and system log data generated as part of platform
          operations. Such information helps maintain system performance,
          reliability, and security.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Purpose of Processing
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Personal data collected through the Services is processed only for
          legitimate and necessary purposes. These purposes include providing
          and maintaining the platform, managing user accounts, enabling
          authentication and secure access to the system, delivering customer
          support, improving the functionality and performance of the Services,
          maintaining system security, preventing fraud or misuse, and complying
          with legal and regulatory obligations.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Legal Basis for Processing
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Kivunova processes personal data on lawful grounds consistent with
          applicable data protection requirements. In most cases, processing is
          necessary for the performance of a contract with the user or
          organization using the Services. Processing may also occur based on
          the consent of the data subject, compliance with legal obligations, or
          legitimate interests in operating, securing, and improving the
          platform.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Disclosure of Personal Data
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Kivunova does not sell personal data. Personal data may be shared with
          trusted third-party service providers when necessary for the operation
          of the Services. These service providers may include cloud
          infrastructure providers, database hosting providers, payment
          processing services, and technology partners that support system
          monitoring and functionality. Personal data may also be disclosed to
          regulatory authorities where required by applicable law. Any
          third-party service providers engaged by Kivunova are required to
          process personal data under appropriate contractual obligations and
          security safeguards.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          International Transfers
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Some infrastructure or service providers used to operate the platform
          may process or store personal data outside Rwanda. When such transfers
          occur, Kivunova ensures that appropriate safeguards are implemented to
          maintain the protection of personal data in accordance with applicable
          legal requirements.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Data Security
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Kivunova implements technical and organizational measures designed to
          protect personal data against unauthorized access, loss, alteration,
          or disclosure. These measures include encrypted communications through
          secure protocols, authentication and access control mechanisms,
          monitoring and logging of system activity, and infrastructure security
          protections within the hosting environment. The platform also
          implements safeguards designed to minimize the exposure of personally
          identifiable information within application logs and monitoring
          systems.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Data Retention
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Personal data is retained only for as long as necessary to provide the
          Services, comply with legal obligations, resolve disputes, and
          maintain the security and integrity of the platform. When personal
          data is no longer required for these purposes, it is securely deleted
          or anonymized in accordance with applicable data protection practices.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Cookies and Website Technologies
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          The Kivunova website and related services may use cookies or similar
          technologies to maintain secure user sessions, remember preferences,
          improve website functionality, and analyze usage patterns. Users may
          control or disable cookies through their browser settings if they
          prefer.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Data Subject Rights
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Subject to applicable data protection laws, individuals may have the
          right to request access to their personal data, request correction of
          inaccurate information, request deletion of personal data where
          appropriate, or withdraw consent when processing is based on consent.
          Requests relating to personal data rights may be submitted using the
          contact information provided below.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Updates to this Policy
        </Typography>
        <Typography variant="body2" sx={bodySx}>
          Kivunova may update this Privacy Policy from time to time to reflect
          operational, legal, or regulatory changes. When updates occur, the
          revised version will be published on this page along with the updated
          effective date.
        </Typography>

        <Typography variant="h6" sx={sectionTitleSx}>
          Contact
        </Typography>
        <Typography variant="body2" sx={{ ...bodySx, marginBottom: 4 }}>
          Questions regarding this Privacy Policy or personal data processing
          practices may be directed to Kivunova Ltd. through the contact
          information available on our website.
        </Typography>
      </PageContent>
    </section>
  );
};

export default PrivacyPolicy;
